import { toCsv } from './csv'
import { OWNER_STAFF_ID, salonDayFor } from './salonDays'
import type { AgendaAppointment } from '../types/agenda'
import type { OpeningDay, SalonDayException } from '../types/site'

/**
 * Urenoverzicht per collega per maand (docs/KASSA-ABONNEMENTEN.md §4), om naast haar factuur te
 * leggen. Geen omzet: alle omzet is van Beauty Bewust. Puur.
 *
 * - Gewerkte dag = een salondag van haar (vaste week of afwijkende dag) tot en met `upTo`.
 * - Begin en eind = de tijden van die salondag.
 * - Behandelingen: afgerond, niet gekomen en geannuleerd apart, per dag en in totaal.
 */
export type StaffHoursDay = {
  date: string
  open: string
  close: string
  minutes: number
  exception: boolean
  done: number
  noShow: number
  cancelled: number
}

export type StaffHoursMonth = {
  staffId: string
  /** 'YYYY-MM' */
  month: string
  days: StaffHoursDay[]
  totals: { days: number; minutes: number; done: number; noShow: number; cancelled: number }
}

const minutesOf = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}

/** Alle datums ('YYYY-MM-DD') van een maand. */
export function daysOfMonth(month: string): string[] {
  const [y, m] = month.split('-').map(Number)
  const count = new Date(y, m, 0).getDate()
  return Array.from({ length: count }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`)
}

export function staffHoursMonth(
  hours: readonly OpeningDay[],
  exceptions: readonly SalonDayException[] | undefined,
  appointments: readonly AgendaAppointment[],
  staffId: string,
  month: string,
  upTo: string,
): StaffHoursMonth {
  const mine = appointments.filter((a) => (a.staffId || OWNER_STAFF_ID) === staffId && a.date.startsWith(month))
  const days: StaffHoursDay[] = []
  for (const date of daysOfMonth(month)) {
    if (date > upTo) break
    const day = salonDayFor(hours, exceptions, date)
    const ofDay = mine.filter((a) => a.date === date)
    const done = ofDay.filter((a) => a.outcome === 'afgerond').length
    // Ook een dag zonder salondag maar mét een afgeronde behandeling telt (bijv. een ruil die niet is ingevoerd).
    if (!day || day.staffId !== staffId) {
      if (done === 0) continue
    }
    const open = day && day.staffId === staffId ? day.open : ofDay.map((a) => a.time).sort()[0]
    const close = day && day.staffId === staffId ? day.close : open
    days.push({
      date,
      open,
      close,
      minutes: Math.max(0, minutesOf(close) - minutesOf(open)),
      exception: Boolean(day?.exception) || !day || day.staffId !== staffId,
      done,
      noShow: ofDay.filter((a) => a.outcome === 'no-show').length,
      cancelled: ofDay.filter((a) => a.outcome === 'geannuleerd').length,
    })
  }
  const sum = (k: 'minutes' | 'done' | 'noShow' | 'cancelled') => days.reduce((s, d) => s + d[k], 0)
  return {
    staffId,
    month,
    days,
    totals: { days: days.length, minutes: sum('minutes'), done: sum('done'), noShow: sum('noShow'), cancelled: sum('cancelled') },
  }
}

/** "7:30" — uren en minuten, voor scherm, print en CSV. */
export const hoursLabel = (minutes: number) => `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`

/** CSV voor naast de factuur: één regel per dag en een totaalregel. */
export function staffHoursCsv(m: StaffHoursMonth, name: string): string {
  return toCsv(
    ['Medewerker', 'Datum', 'Begin', 'Eind', 'Uren', 'Afgerond', 'Niet gekomen', 'Geannuleerd', 'Afwijkende dag'],
    [
      ...m.days.map((d) => [name, d.date, d.open, d.close, hoursLabel(d.minutes), d.done, d.noShow, d.cancelled, d.exception ? 'ja' : '']),
      [name, 'Totaal', null, null, hoursLabel(m.totals.minutes), m.totals.done, m.totals.noShow, m.totals.cancelled, null],
    ],
  )
}
